import { useEffect, useState, useMemo } from "react";
import { View, ScrollView, StyleSheet, Pressable, ActivityIndicator, TextInput, Platform } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { ModernCard } from "@/components/crew";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { DateInput } from "@/components/ui/DateInput";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import Avatar from "@/components/Avatar";
import { PhotoCapture, PhotoFile, photoFileToBase64 } from "@/components/PhotoCapture";
import useAuth from "@/hooks/useAuth";
import { shiftsService } from "@/services/shiftService";
import { ShiftType } from "@/types/db/shifts";
import { supabase } from "@/lib/supabase-client";
import useToast from "@/hooks/useToast";
import { useModal } from "@/hooks/useModal";
import { RemarkModal } from "@/components/Modals/RemarkModal";

import { ShiftUser } from "@/types/db/shift_users";

interface ShiftSurveyScreenProps {
  shiftId: string;
}

export default function ShiftSurveyScreen({ shiftId }: ShiftSurveyScreenProps) {
  const { colors } = useTheme();
  const { user, displayNames } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const shiftName = displayNames?.shifts?.name || "Shift";

  // Loading state
  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState<ShiftType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Section 1: End Time
  const [endTime, setEndTime] = useState<Date>(new Date());

  // Section 2: No Shows
  const [hasNoShows, setHasNoShows] = useState(false);
  const [selectedNoShows, setSelectedNoShows] = useState<Set<string>>(new Set());
  const [noShowSearchQuery, setNoShowSearchQuery] = useState("");

  // Section 3: Attendance Issues (Late Arrivals & Early Departures)
  const [hasAttendanceIssues, setHasAttendanceIssues] = useState(false);
  const [selectedLate, setSelectedLate] = useState<Set<string>>(new Set());
  const [selectedEarlyLeave, setSelectedEarlyLeave] = useState<Set<string>>(new Set());
  const [attendanceSearchQuery, setAttendanceSearchQuery] = useState("");

  // Section 5: Extras
  const [hasExtras, setHasExtras] = useState(false);
  const [extrasCount, setExtrasCount] = useState("0");
  const [extrasRemark, setExtrasRemark] = useState("");

  // Section 6: Standouts
  const [hasStandouts, setHasStandouts] = useState(false);
  const [selectedStandouts, setSelectedStandouts] = useState<Set<string>>(new Set());
  const [standoutSearchQuery, setStandoutSearchQuery] = useState("");

  // Per-item remarks (keyed by "section:userId", e.g. "noshow:abc", "standout:abc")
  const [remarks, setRemarks] = useState<Record<string, string>>({});

  // Section 8: Notes & Photos
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState<PhotoFile[]>([]);

  // Fetch shift data
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!shiftId) return;
      setLoading(true);
      try {
        const row = (await shiftsService.getShiftById(shiftId)) as ShiftType | null;
        if (!alive) return;
        setShift(row);
        if (row?.end_time) {
          setEndTime(new Date(row.end_time));
        }
      } catch (e) {
        console.error("Failed to load shift:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [shiftId]);

  // Redirect if shift is already complete
  useEffect(() => {
    if (shift?.status === "COMPLETE") {
      toast.showToast({
        title: "Survey Already Submitted",
        message: "This shift has already been completed.",
        toastType: "info",
      });
      router.dismiss();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift?.status]);

  // Get accepted users from all positions
  const acceptedUsers = useMemo(() => {
    if (!shift?.shift_positions) return [];
    const users: ShiftUser[] = [];
    shift.shift_positions.forEach((position) => {
      (position.shift_users || []).forEach((su) => {
        if (su.status === "ACCEPTED") {
          users.push(su as ShiftUser);
        }
      });
    });
    return users;
  }, [shift?.shift_positions]);

  // Filter functions for each section
  const filterUsers = (query: string) => {
    if (!query.trim()) return acceptedUsers;
    const q = query.toLowerCase();
    return acceptedUsers.filter((su) => {
      const u = su.user;
      if (!u) return false;
      const name =
        `${u.preferred_name || ""} ${u.first_name || ""} ${u.last_name || ""} ${u.email_address || ""}`.toLowerCase();
      return name.includes(q);
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredNoShowUsers = useMemo(() => filterUsers(noShowSearchQuery), [acceptedUsers, noShowSearchQuery]);

  // Exclude no-shows from attendance and standout sections
  const availableForAttendance = useMemo(
    () => acceptedUsers.filter((su) => !selectedNoShows.has(su.id)),
    [acceptedUsers, selectedNoShows],
  );
  const filteredAttendanceUsers = useMemo(() => {
    if (!attendanceSearchQuery.trim()) return availableForAttendance;
    const q = attendanceSearchQuery.toLowerCase();
    return availableForAttendance.filter((su) => {
      const u = su.user;
      if (!u) return false;
      const name =
        `${u.preferred_name || ""} ${u.first_name || ""} ${u.last_name || ""} ${u.email_address || ""}`.toLowerCase();
      return name.includes(q);
    });
  }, [availableForAttendance, attendanceSearchQuery]);

  const availableForStandouts = useMemo(
    () => acceptedUsers.filter((su) => !selectedNoShows.has(su.id)),
    [acceptedUsers, selectedNoShows],
  );
  const filteredStandoutUsers = useMemo(() => {
    if (!standoutSearchQuery.trim()) return availableForStandouts;
    const q = standoutSearchQuery.toLowerCase();
    return availableForStandouts.filter((su) => {
      const u = su.user;
      if (!u) return false;
      const name =
        `${u.preferred_name || ""} ${u.first_name || ""} ${u.last_name || ""} ${u.email_address || ""}`.toLowerCase();
      return name.includes(q);
    });
  }, [availableForStandouts, standoutSearchQuery]);

  const getDisplayName = (u: ShiftUser["user"]) => {
    if (!u) return "Unknown User";
    return u.preferred_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email_address || "Unknown User";
  };

  // Toggle functions
  const toggleNoShow = (id: string) => {
    setSelectedNoShows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // Clear any other selections for this user since they're now a no-show
        setSelectedLate((p) => {
          const n = new Set(p);
          n.delete(id);
          return n;
        });
        setSelectedEarlyLeave((p) => {
          const n = new Set(p);
          n.delete(id);
          return n;
        });
        setSelectedStandouts((p) => {
          const n = new Set(p);
          n.delete(id);
          return n;
        });
      }
      return next;
    });
  };

  const toggleLate = (id: string) => {
    setSelectedLate((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleEarlyLeave = (id: string) => {
    setSelectedEarlyLeave((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleStandout = (id: string) => {
    if (selectedStandouts.has(id)) {
      // Removing — deselect and clear remark, no modal
      setSelectedStandouts((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setRemarks((prev) => {
        const next = { ...prev };
        delete next[`standout:${id}`];
        return next;
      });
    } else {
      // Adding — open modal, only add if remark is saved
      const su = acceptedUsers.find((u) => u.id === id);
      const displayName = getDisplayName(su?.user);
      openModal(
        <RemarkModal
          title={`Remark for ${displayName}`}
          description="Describe what made this person stand out — positive or negative. Be specific so this feedback can be acted on. Vague or incomplete remarks may be disregarded."
          required
          initialValue=""
          onSave={(value) => {
            if (value.trim()) {
              setSelectedStandouts((prev) => new Set(prev).add(id));
              setRemarks((prev) => ({ ...prev, [`standout:${id}`]: value }));
            }
            closeModal();
          }}
          onCancel={closeModal}
        />,
      );
    }
  };

  const handleExtrasChange = (text: string) => {
    const cleaned = text.replace(/[^\d]/g, "");
    setExtrasCount(cleaned || "0");
  };

  const openRemarkModal = (remarkKey: string, displayName: string) => {
    openModal(
      <RemarkModal
        title={`Remark for ${displayName}`}
        initialValue={remarks[remarkKey] || ""}
        onSave={(value) => {
          setRemarks((prev) => {
            const next = { ...prev };
            if (value.trim()) {
              next[remarkKey] = value;
            } else {
              delete next[remarkKey];
            }
            return next;
          });
          closeModal();
        }}
        onCancel={closeModal}
      />,
    );
  };

  const handleSubmit = async () => {
    if (!shiftId || !user?.id) return;

    setIsSubmitting(true);
    try {
      // Convert photos to base64 for edge function upload
      const photoData = await Promise.all(photos.map(photoFileToBase64));

      // Build timesheet entries from accepted users (excluding no-shows)
      const entries = acceptedUsers
        .filter((su) => !selectedNoShows.has(su.id))
        .map((su) => ({
          user_id: su.id,
          start_time: shift?.start_time || new Date().toISOString(),
          end_time: endTime.toISOString(),
          notes: notes || undefined,
        }));

      // Build user info arrays for incidents (exclude no-shows)
      const lateArrivals = acceptedUsers
        .filter((su) => selectedLate.has(su.id) && !selectedNoShows.has(su.id))
        .map((su) => ({
          id: su.id,
          first_name: su.user?.first_name,
          last_name: su.user?.last_name,
          remark: remarks[`attendance:${su.id}`] || undefined,
        }));

      const earlyDepartures = acceptedUsers
        .filter((su) => selectedEarlyLeave.has(su.id) && !selectedNoShows.has(su.id))
        .map((su) => ({
          id: su.id,
          first_name: su.user?.first_name,
          last_name: su.user?.last_name,
          remark: remarks[`attendance:${su.id}`] || undefined,
        }));

      const standouts = acceptedUsers
        .filter((su) => selectedStandouts.has(su.id) && !selectedNoShows.has(su.id))
        .map((su) => ({
          id: su.id,
          first_name: su.user?.first_name,
          last_name: su.user?.last_name,
          remark: remarks[`standout:${su.id}`] || undefined,
        }));

      const noShows = acceptedUsers
        .filter((su) => selectedNoShows.has(su.id))
        .map((su) => ({
          id: su.id,
          first_name: su.user?.first_name,
          last_name: su.user?.last_name,
          remark: remarks[`noshow:${su.id}`] || undefined,
        }));

      // Call submit-timesheet edge function
      const { data, error } = await supabase.functions.invoke("submit-timesheet", {
        body: {
          shift_id: shiftId,
          entries,
          no_shows: noShows,
          late_arrivals: lateArrivals,
          early_departures: earlyDepartures,
          standouts,
          extras_count: hasExtras ? parseInt(extrasCount) : 0,
          extras_remark: hasExtras && extrasRemark ? extrasRemark : undefined,
          survey_notes: notes || undefined,
          photos: photoData,
        },
      });

      if (error) {
        toast.showToast({
          title: "Submission Failed",
          message: error.message,
          toastType: "error",
        });
        console.error("Edge function error:", error);
        return;
      }

      // Toast the response with summary
      if (data?.success) {
        const parts: string[] = [];
        parts.push(`${data.timecards_created} timecard${data.timecards_created !== 1 ? "s" : ""}`);
        if (data.no_shows_count > 0)
          parts.push(`${data.no_shows_count} no-show${data.no_shows_count !== 1 ? "s" : ""}`);
        if (data.late_arrivals_count > 0) parts.push(`${data.late_arrivals_count} late`);
        if (data.early_departures_count > 0) parts.push(`${data.early_departures_count} early`);
        if (data.extras_count > 0) parts.push(`${data.extras_count} extra${data.extras_count !== 1 ? "s" : ""}`);
        if (data.incidents_created > 0)
          parts.push(`${data.incidents_created} incident${data.incidents_created !== 1 ? "s" : ""}`);
        if (data.photos_uploaded > 0)
          parts.push(`${data.photos_uploaded} photo${data.photos_uploaded !== 1 ? "s" : ""}`);

        toast.showToast({
          title: "Timesheets Submitted",
          message: parts.join(" · "),
          toastType: "success",
        });
      } else {
        toast.showToast({
          title: "Verification Failed",
          message: data?.message || "Check console for details",
          toastType: "error",
        });
      }

      console.log("Edge function response:", data);
      console.log("Survey submission:", {
        shiftId,
        endTime: endTime.toISOString(),
        noShows: Array.from(selectedNoShows),
        lateArrivals: Array.from(selectedLate),
        earlyDepartures: Array.from(selectedEarlyLeave),
        extrasCount: hasExtras ? parseInt(extrasCount) : 0,
        standouts: Array.from(selectedStandouts),
        notes,
        photoCount: photos.length,
      });

      router.dismiss();
    } catch (e) {
      console.error("Failed to submit survey:", e);
      toast.showToast({
        title: "Submission Error",
        message: e instanceof Error ? e.message : "An unexpected error occurred",
        toastType: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    router.dismiss();
  };

  // Render user selection list
  const renderUserList = (
    users: ShiftUser[],
    selected: Set<string>,
    onToggle: (id: string) => void,
    colorScheme: { bg: string; border: string; check: string },
    searchQuery: string,
    section: string,
    icon?: string,
  ) => {
    if (users.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons name="account-search" size={32} color={colors.placeholder} />
          <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>
            {searchQuery ? "No matching team members" : "No accepted team members"}
          </ThemedText>
        </View>
      );
    }

    return (
      <View style={styles.userList}>
        {users.map((su) => {
          const isSelected = selected.has(su.id);
          const remarkKey = `${section}:${su.id}`;
          const hasRemark = !!remarks[remarkKey];
          return (
            <Pressable
              key={su.id}
              onPress={() => onToggle(su.id)}
              style={({ pressed, hovered }) => [
                styles.userItem,
                {
                  backgroundColor: isSelected ? colorScheme.bg : colors.background,
                  borderColor: isSelected ? colorScheme.border : colors.border,
                },
                hovered && !isSelected && { backgroundColor: colors.card },
                pressed && { opacity: 0.7 },
              ]}
            >
              <Avatar userId={su.user?.id} firstName={su.user?.first_name} lastName={su.user?.last_name} size={36} />
              <View style={styles.userInfo}>
                <ThemedText style={styles.userName}>{getDisplayName(su.user)}</ThemedText>
              </View>
              {isSelected && (
                <Pressable
                  onPress={(e) => {
                    e.stopPropagation();
                    openRemarkModal(remarkKey, getDisplayName(su.user));
                  }}
                  hitSlop={8}
                  style={styles.remarkButton}
                >
                  <MaterialCommunityIcons
                    name={hasRemark ? "note-text" : "note-edit-outline"}
                    size={20}
                    color={hasRemark ? colorScheme.check : colors.textSecondary}
                  />
                </Pressable>
              )}
              <View
                style={[
                  styles.checkbox,
                  {
                    backgroundColor: isSelected ? colorScheme.check : "transparent",
                    borderColor: isSelected ? colorScheme.check : colors.border,
                  },
                ]}
              >
                {isSelected && <MaterialCommunityIcons name={(icon || "check") as any} size={14} color="white" />}
              </View>
            </Pressable>
          );
        })}
      </View>
    );
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.backgroundSecondary }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled"
    >
      {/* Section 1: End Time */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="clock-end" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            {shiftName} End Time
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          What time did this {shiftName.toLowerCase()} actually end?
        </ThemedText>
        <DateInput value={endTime} onChange={setEndTime} mode="datetime" label="Actual End Time" />
      </ModernCard>

      {/* Section 2: No Shows */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="account-off" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            No Shows
          </ThemedText>
        </View>
        <View style={styles.toggleRow}>
          <ThemedText style={styles.toggleLabel}>Did anyone not show up?</ThemedText>
          <ToggleSwitch value={hasNoShows} onValueChange={setHasNoShows} />
        </View>
        {hasNoShows && (
          <View style={styles.userSection}>
            <TextInput
              style={[
                styles.searchInput,
                { backgroundColor: colors.background, borderColor: colors.border, color: colors.text },
              ]}
              placeholder="Search team members..."
              placeholderTextColor={colors.placeholder}
              value={noShowSearchQuery}
              onChangeText={setNoShowSearchQuery}
            />
            {renderUserList(
              filteredNoShowUsers,
              selectedNoShows,
              toggleNoShow,
              {
                bg: colors.ruby?.background || "#fef2f2",
                border: colors.ruby?.border || "#fca5a5",
                check: colors.ruby?.text || "#dc2626",
              },
              noShowSearchQuery,
              "noshow",
            )}
            {selectedNoShows.size > 0 && (
              <View style={[styles.selectedCount, { backgroundColor: colors.ruby?.background || "#fef2f2" }]}>
                <MaterialCommunityIcons name="alert-circle" size={16} color={colors.ruby?.text || "#dc2626"} />
                <ThemedText style={{ color: colors.ruby?.text || "#dc2626", fontSize: 13 }}>
                  {selectedNoShows.size} no-show{selectedNoShows.size > 1 ? "s" : ""} selected
                </ThemedText>
              </View>
            )}
          </View>
        )}
      </ModernCard>

      {/* Section 3: Attendance Issues */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="clock-alert" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Attendance Issues
          </ThemedText>
        </View>
        <View style={styles.toggleRow}>
          <ThemedText style={styles.toggleLabel}>Was anyone late or leave early?</ThemedText>
          <ToggleSwitch value={hasAttendanceIssues} onValueChange={setHasAttendanceIssues} />
        </View>
        {hasAttendanceIssues && (
          <View style={styles.userSection}>
            <TextInput
              style={[
                styles.searchInput,
                { backgroundColor: colors.background, borderColor: colors.border, color: colors.text },
              ]}
              placeholder="Search team members..."
              placeholderTextColor={colors.placeholder}
              value={attendanceSearchQuery}
              onChangeText={setAttendanceSearchQuery}
            />
            <View style={{ gap: 6 }}>
              {filteredAttendanceUsers.map((su) => {
                const isLate = selectedLate.has(su.id);
                const isEarly = selectedEarlyLeave.has(su.id);
                const attendanceRemarkKey = `attendance:${su.id}`;
                const hasRemark = !!remarks[attendanceRemarkKey];
                return (
                  <View
                    key={su.id}
                    style={[
                      styles.userRow,
                      {
                        backgroundColor: isLate || isEarly ? colors.amber?.background || "#fffbeb" : colors.background,
                        borderColor: isLate || isEarly ? colors.amber?.border || "#fcd34d" : colors.border,
                      },
                    ]}
                  >
                    <Avatar
                      userId={su.user?.id}
                      firstName={su.user?.first_name}
                      lastName={su.user?.last_name}
                      size={32}
                    />
                    <View style={[styles.userInfo, { flex: 1 }]}>
                      <ThemedText style={styles.userName}>{getDisplayName(su.user)}</ThemedText>
                    </View>
                    <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
                      <Pressable
                        onPress={() => toggleLate(su.id)}
                        style={[
                          styles.attendanceChip,
                          {
                            backgroundColor: isLate ? colors.amber?.text || "#d97706" : colors.background,
                            borderColor: colors.amber?.border || "#fcd34d",
                          },
                        ]}
                      >
                        <MaterialCommunityIcons
                          name="clock-alert"
                          size={14}
                          color={isLate ? "white" : colors.amber?.text || "#d97706"}
                        />
                        <ThemedText
                          style={{
                            fontSize: 11,
                            fontWeight: "600",
                            color: isLate ? "white" : colors.amber?.text || "#d97706",
                          }}
                        >
                          Late
                        </ThemedText>
                      </Pressable>
                      <Pressable
                        onPress={() => toggleEarlyLeave(su.id)}
                        style={[
                          styles.attendanceChip,
                          {
                            backgroundColor: isEarly ? colors.amber?.text || "#d97706" : colors.background,
                            borderColor: colors.amber?.border || "#fcd34d",
                          },
                        ]}
                      >
                        <MaterialCommunityIcons
                          name="exit-run"
                          size={14}
                          color={isEarly ? "white" : colors.amber?.text || "#d97706"}
                        />
                        <ThemedText
                          style={{
                            fontSize: 11,
                            fontWeight: "600",
                            color: isEarly ? "white" : colors.amber?.text || "#d97706",
                          }}
                        >
                          Early
                        </ThemedText>
                      </Pressable>
                      {(isLate || isEarly) && (
                        <Pressable
                          onPress={() => openRemarkModal(attendanceRemarkKey, getDisplayName(su.user))}
                          hitSlop={8}
                          style={styles.remarkButton}
                        >
                          <MaterialCommunityIcons
                            name={hasRemark ? "note-text" : "note-edit-outline"}
                            size={20}
                            color={hasRemark ? colors.amber?.text || "#d97706" : colors.textSecondary}
                          />
                        </Pressable>
                      )}
                    </View>
                  </View>
                );
              })}
            </View>
            {(selectedLate.size > 0 || selectedEarlyLeave.size > 0) && (
              <View style={[styles.selectedCount, { backgroundColor: colors.amber?.background || "#fffbeb" }]}>
                <MaterialCommunityIcons name="alert-circle" size={16} color={colors.amber?.text || "#d97706"} />
                <ThemedText style={{ color: colors.amber?.text || "#d97706", fontSize: 13 }}>
                  {selectedLate.size > 0 && `${selectedLate.size} late`}
                  {selectedLate.size > 0 && selectedEarlyLeave.size > 0 && " · "}
                  {selectedEarlyLeave.size > 0 && `${selectedEarlyLeave.size} early`}
                </ThemedText>
              </View>
            )}
          </View>
        )}
      </ModernCard>

      {/* Section 5: Extras */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="account-plus" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Extra Team Members
          </ThemedText>
        </View>
        <View style={styles.toggleRow}>
          <ThemedText style={styles.toggleLabel}>Did you have any extras?</ThemedText>
          <ToggleSwitch value={hasExtras} onValueChange={setHasExtras} />
        </View>
        {hasExtras && (
          <View style={styles.extrasSection}>
            <View style={styles.extrasRow}>
              <ThemedText style={styles.extrasLabel}>Number of extras:</ThemedText>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <View style={[styles.extrasInput, { borderColor: colors.border, backgroundColor: colors.background }]}>
                  <Pressable
                    onPress={() => setExtrasCount(String(Math.max(0, parseInt(extrasCount) - 1)))}
                    style={[styles.stepperBtn, { backgroundColor: colors.card }]}
                  >
                    <MaterialCommunityIcons name="minus" size={20} color={colors.text} />
                  </Pressable>
                  <TextInput
                    style={[styles.extrasField, { color: colors.text }]}
                    value={extrasCount}
                    onChangeText={handleExtrasChange}
                    keyboardType="numeric"
                    textAlign="center"
                  />
                  <Pressable
                    onPress={() => setExtrasCount(String(parseInt(extrasCount) + 1))}
                    style={[styles.stepperBtn, { backgroundColor: colors.card }]}
                  >
                    <MaterialCommunityIcons name="plus" size={20} color={colors.text} />
                  </Pressable>
                </View>
                <Pressable
                  onPress={() => {
                    openModal(
                      <RemarkModal
                        title="Remark for Extras"
                        initialValue={extrasRemark}
                        onSave={(value) => {
                          setExtrasRemark(value.trim() ? value : "");
                          closeModal();
                        }}
                        onCancel={closeModal}
                      />,
                    );
                  }}
                  hitSlop={8}
                  style={styles.remarkButton}
                >
                  <MaterialCommunityIcons
                    name={extrasRemark ? "note-text" : "note-edit-outline"}
                    size={20}
                    color={extrasRemark ? colors.primary : colors.textSecondary}
                  />
                </Pressable>
              </View>
            </View>
          </View>
        )}
      </ModernCard>

      {/* Section 6: Standouts */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="account-star" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Standouts
          </ThemedText>
        </View>
        <View style={styles.toggleRow}>
          <ThemedText style={styles.toggleLabel}>Any standouts to report?</ThemedText>
          <ToggleSwitch value={hasStandouts} onValueChange={setHasStandouts} />
        </View>
        {hasStandouts && (
          <View style={styles.userSection}>
            <ThemedText style={[styles.standoutHint, { color: colors.textSecondary }]}>
              Select {displayNames?.users?.plural?.toLowerCase() || "team members"} for positive or negative feedback
            </ThemedText>
            <TextInput
              style={[
                styles.searchInput,
                { backgroundColor: colors.background, borderColor: colors.border, color: colors.text },
              ]}
              placeholder={`Search ${displayNames?.users?.plural?.toLowerCase() || "team members"}...`}
              placeholderTextColor={colors.placeholder}
              value={standoutSearchQuery}
              onChangeText={setStandoutSearchQuery}
            />
            {filteredStandoutUsers.length === 0 ? (
              <View style={styles.emptyContainer}>
                <MaterialCommunityIcons name="account-search" size={32} color={colors.placeholder} />
                <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>
                  {standoutSearchQuery ? "No matching team members" : "No accepted team members"}
                </ThemedText>
              </View>
            ) : (
              <View style={styles.userList}>
                {filteredStandoutUsers.map((su) => {
                  const isSelected = selectedStandouts.has(su.id);
                  const remarkKey = `standout:${su.id}`;
                  const remarkText = remarks[remarkKey];
                  const standoutColors = {
                    bg: colors.violet?.background || "#f5f3ff",
                    border: colors.violet?.border || "#c4b5fd",
                    check: colors.violet?.text || "#7c3aed",
                  };
                  return (
                    <Pressable
                      key={su.id}
                      onPress={() => toggleStandout(su.id)}
                      style={({ pressed, hovered }) => [
                        styles.userItem,
                        {
                          backgroundColor: isSelected ? standoutColors.bg : colors.background,
                          borderColor: isSelected ? standoutColors.border : colors.border,
                        },
                        hovered && !isSelected && { backgroundColor: colors.card },
                        pressed && { opacity: 0.7 },
                      ]}
                    >
                      <Avatar
                        userId={su.user?.id}
                        firstName={su.user?.first_name}
                        lastName={su.user?.last_name}
                        size={36}
                      />
                      <View style={styles.userInfo}>
                        <ThemedText style={styles.userName}>{getDisplayName(su.user)}</ThemedText>
                        {isSelected && remarkText && (
                          <Pressable
                            onPress={(e) => {
                              e.stopPropagation();
                              openModal(
                                <RemarkModal
                                  title={`Remark for ${getDisplayName(su.user)}`}
                                  description="Describe what made this person stand out — positive or negative. Be specific so this feedback can be acted on. Vague or incomplete remarks may be disregarded."
                                  required
                                  initialValue={remarkText}
                                  onSave={(value) => {
                                    if (value.trim()) {
                                      setRemarks((prev) => ({ ...prev, [remarkKey]: value }));
                                    }
                                    closeModal();
                                  }}
                                  onCancel={closeModal}
                                />,
                              );
                            }}
                          >
                            <ThemedText
                              numberOfLines={1}
                              style={{ fontSize: 12, color: standoutColors.check, marginTop: 2 }}
                            >
                              {remarkText}
                            </ThemedText>
                          </Pressable>
                        )}
                      </View>
                      <View
                        style={[
                          styles.checkbox,
                          {
                            backgroundColor: isSelected ? standoutColors.check : "transparent",
                            borderColor: isSelected ? standoutColors.check : colors.border,
                          },
                        ]}
                      >
                        {isSelected && <MaterialCommunityIcons name="star" size={14} color="white" />}
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            )}
            {selectedStandouts.size > 0 && (
              <View style={[styles.selectedCount, { backgroundColor: colors.violet?.background || "#f5f3ff" }]}>
                <MaterialCommunityIcons name="account-star" size={16} color={colors.violet?.text || "#7c3aed"} />
                <ThemedText style={{ color: colors.violet?.text || "#7c3aed", fontSize: 13 }}>
                  {selectedStandouts.size} standout{selectedStandouts.size > 1 ? "s" : ""} selected
                </ThemedText>
              </View>
            )}
          </View>
        )}
      </ModernCard>

      {/* Section 8: Notes */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="note-text" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Timesheet Notes
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          Include anything related to time and attendance for this {shiftName.toLowerCase()}.
        </ThemedText>
        <TextInput
          style={[
            styles.notesInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          value={notes}
          onChangeText={setNotes}
          placeholder="Enter any notes about time and attendance..."
          placeholderTextColor={colors.placeholder}
          multiline
          numberOfLines={6}
          textAlignVertical="top"
        />
      </ModernCard>

      {/* Section 9: Photos */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="camera" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Timesheet Photo
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          Upload a photo of the timesheet (required).
        </ThemedText>
        <PhotoCapture
          photos={photos}
          onPhotosChange={setPhotos}
          maxPhotos={10}
          placeholder="Tap to add timesheet photo"
          disabled={isSubmitting}
        />
      </ModernCard>

      {/* Action Buttons */}
      <View style={styles.buttonRow}>
        <Button
          text="Cancel"
          icon="close"
          iconPosition="left"
          onPress={handleCancel}
          style={styles.button}
          enabled={!isSubmitting}
        />
        <Button
          text={isSubmitting ? "Submitting..." : "Submit Survey"}
          icon="check"
          iconPosition="left"
          onPress={handleSubmit}
          style={[styles.button, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          enabled={!isSubmitting && photos.length > 0}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    padding: 16,
    paddingBottom: 32,
    gap: 16,
  },
  card: {},
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: "500",
  },
  userSection: {
    marginTop: 16,
    gap: 12,
  },
  searchInput: {
    height: 44,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  userList: {
    gap: 8,
  },
  userItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 14,
    fontWeight: "500",
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  userRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  attendanceChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
  },
  selectedCount: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 10,
    borderRadius: 8,
  },
  extrasSection: {
    marginTop: 16,
  },
  extrasRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  extrasLabel: {
    fontSize: 14,
    fontWeight: "500",
  },
  extrasInput: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    borderWidth: 1,
    overflow: "hidden",
  },
  stepperBtn: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  extrasField: {
    width: 50,
    height: 40,
    fontSize: 16,
    fontWeight: "600",
  },
  notesInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 120,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
  },
  button: {
    flex: 1,
    height: 48,
  },
  standoutHint: {
    fontSize: 13,
    fontStyle: "italic",
    marginBottom: 4,
  },
  remarkButton: {
    padding: 4,
  },
});
